import { doRequest } from '@r1-frontend/do-request';
import {
    ERequestStatus,
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import { getEquipmentsListByProps } from '~/src/features/full-buy/helpers';

import { IAdditionalProduct } from './store/helpers/tariffTransformerHelpers';
import { TProduct } from './store/transformShopItem';
import { MEGABIT_AVAIL_CODES_LIST, MEGABIT_SPEED } from './consts';

interface IGetErrorMessage {
    isDouble?: boolean,
    isFromCache?: boolean,
    selfErrMsg?: string,
}

export const getErrorMessage = ({ isDouble = false, isFromCache = false, selfErrMsg }: IGetErrorMessage): string => {
    enum EMessages {
        DOUBLE_AGREEMENT_MSG = 'Договор создан, но к указанному номеру телефона уже привязан другой договор. Специалист перезвонит вам для уточнения деталей.', // Не используемое сообщение, уточнить в каком случае должно выводиться
        FROM_CACHE_MSG = 'Вы уже оставили заявку, ожидайте звонка специалиста',
        SKIP_DOUBLE_AGREEMENT_MSG = 'Мы получили вашу заявку, но по указанному адресу есть активный договор. Специалист позвонит вам для уточнения деталей.',
        ERROR_REQUEST_AGREEMENT_MSG = 'Не удалось создать договор. Наш специалист перезвонит вам для решения вопроса.',
        SKIP_ERROR_REQUEST_AGREEMENT_MSG = 'Не удалось оставить заявку. Наш специалист перезвонит вам для решения вопроса.',
    }

    if (selfErrMsg) {
        return EMessages.SKIP_ERROR_REQUEST_AGREEMENT_MSG;
    }
    if (isFromCache) {
        return EMessages.FROM_CACHE_MSG;
    }
    if (isDouble) {
        return EMessages.SKIP_DOUBLE_AGREEMENT_MSG;
    }
    return EMessages.SKIP_ERROR_REQUEST_AGREEMENT_MSG;
};

interface ISubmitRequestProps {
    isMono: boolean,
    fio: string,
    phone: string,
    csrf: string,
    sale_package_id: number | undefined,
    tariff_name: string | undefined,
    additionalProducts: IAdditionalProduct[],
    products: TProduct[],
    street?: string,
    house?: string,
    flat?: string,
    address?: string,
    shouldConnect: boolean,
}

/**
 * Отправка заявки на подключение
 */
export const submitRequest = async({
    isMono,
    fio,
    phone,
    csrf,
    sale_package_id,
    tariff_name,
    additionalProducts,
    products,
    shouldConnect,
    ...restData
}: ISubmitRequestProps) => {
    const requiredAdditionalProducts = additionalProducts.filter(el => el.required);

    const equipmentsList = getEquipmentsListByProps([...products, ...requiredAdditionalProducts]);

    const dataParams: RequestDto = {
        check_call_type: window.location.pathname,
        fio,
        phone,
        plan: sale_package_id,
        product_id: [EProductsId.internet],
        router: equipmentsList.router,
        cost_type: equipmentsList.cost_type, // рассрочка или собственность
        leasing_duration: equipmentsList.leasing_duration, // Срок рассрочки
        csrf,
        ...restData,
    };

    if (!isMono) {
        dataParams.product_id.push(EProductsId.tv);
    }

    const isAgreement = shouldConnect;

    const resp = await doRequest.apiRequest<ResponseDto>(requestByConnection(dataParams));
    if (resp.isSuccess) {
        const { status, message } = resp.payload;
        const ok = !!status;

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.applicationForm,
            action: ok ? ACTIONS.finished : ACTIONS.error,
            label: `${isAgreement ? 'create agreement' : 'address verification'} ${tariff_name} ${message}`,
        });

        return { ok, message, status: status ? ERequestStatus.success : ERequestStatus.error };
    } else {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.applicationForm,
            action: ACTIONS.error,
            label: `${isAgreement ? 'create agreement' : 'address verification'} ${tariff_name}`,
        });

        return {
            ok: false,
            message: 'Не удалось отправить заявку.',
            status: ERequestStatus.error,
            id: undefined,
        };
    }
};

/**
 * Требуется ли гигабитная сеть для тарифа
 */
export const isGigaSpeedRequired = (tariffSpeed: number) => tariffSpeed > MEGABIT_SPEED;

/**
 * Есть ли гигабитная сеть на адресе
 */
export const isGigabitCanConnected = (gigabitAvail: number) => MEGABIT_AVAIL_CODES_LIST.includes(gigabitAvail);

/**
 * Возвращает можем ли подключить выбранный тариф, после проверки адреса
 */
export const isTariffCanConnected = (
    isHouseConnected: boolean,
    requiredGigaSpeed: boolean,
    gigabitCanConnect: boolean,
) => {
    return requiredGigaSpeed
        ? (isHouseConnected && gigabitCanConnect)
        : isHouseConnected;
};

/* Получаем стоимость обязательного оборудования */
export const getAdditionalProductsCosts = (additionalProducts: IAdditionalProduct[]): { monthly: number, once: number } => {
    const requiredProducts = additionalProducts.filter(product => product.required);
    return requiredProducts.reduce<{ monthly: number, once: number }>((acc, curr) => {
        const { selectedPrice } = curr;
        if (!selectedPrice) {
            return { monthly: 0, once: 0 };
        }

        return {
            monthly: acc.monthly + (selectedPrice.duration ? selectedPrice.cost : 0),
            once: acc.once + (!selectedPrice.duration ? selectedPrice.cost : 0),
        };
    }, { monthly: 0, once: 0 });
};

/**
 * Подсчитывает общую стоимость продуктов (рассрочка и единовременно)
 */
export const getProductsCosts = (products: TProduct[]): { monthly: number, once: number } => {
    const productsWithQuantity = products.filter(product => product.quantity);

    return productsWithQuantity.reduce((acc, curr) => {
        const { selectedPrice, quantity } = curr;
        if (!selectedPrice) {
            return acc;
        }

        const isInstallment = !!selectedPrice.duration;

        const currCost = selectedPrice.cost * quantity;

        return {
            monthly: isInstallment ? acc.monthly + currCost : acc.monthly,
            once: isInstallment ? acc.once : acc.once + currCost,
        };
    }, { monthly: 0, once: 0 });
};

/**
 *  Возвращает итоговую месячную и единоразовую стоимость выбранных продуктов / тарифов
 */
export const calculateTotalPrices = (
    price = 0,
    connectPrice = 0,
    products: TProduct[] = [],
): { monthlyPayment: number, oncePayment: number } => {

    // Стоимость выбранного оборудования
    const productsCost = getProductsCosts(products);

    return {
        monthlyPayment: price + productsCost.monthly,
        oncePayment: connectPrice + productsCost.once,
    };
};
